/**
 * WhatsApp Click-to-Chat Link Generator & Attribution Utility for Thinkers Arcade Kenya
 * Constructs pre-filled, URL-encoded WhatsApp API links tailored for regional landing pages,
 * pricing packages, and technical strategy consultations.
 */

export interface WhatsAppLeadOptions {
  phone?: string;
  region: 'Nairobi' | 'Mombasa' | 'Nakuru' | 'Kisumu' | 'Meru' | 'Kenya';
  service: string;
  packageTitle?: string;
  estimatedBudget?: string;
  customMessage?: string;
  sourceUrl?: string;
}

export const OFFICIAL_WHATSAPP_NUMBER = '254114646341';
export const DISPLAY_PHONE_NUMBER = '+254 114 646 341';

/**
 * Creates an attributed WhatsApp click-to-chat URL with pre-filled message
 */
export function createWhatsAppLeadUrl(options: WhatsAppLeadOptions): string {
  const phone = options.phone || OFFICIAL_WHATSAPP_NUMBER;
  
  if (options.customMessage) {
    return `https://wa.me/${phone}?text=${encodeURIComponent(options.customMessage)}`;
  }

  const packageInfo = options.packageTitle ? ` [Package: ${options.packageTitle}]` : '';
  const budgetInfo = options.estimatedBudget ? ` [Budget: KES ${options.estimatedBudget}]` : '';
  const regionTag = options.region !== 'Kenya' ? ` in ${options.region}` : '';

  const defaultGreeting = `Jambo Thinkers Arcade! I am contacting you from ${options.region}${regionTag} regarding ${options.service}${packageInfo}${budgetInfo}. I would like an instant quotation and technical strategy session.`;

  return `https://wa.me/${phone}?text=${encodeURIComponent(defaultGreeting)}`;
}

/**
 * Localized Regional Lead Preset Strings
 */
export const REGIONAL_WHATSAPP_PRESETS: Record<
  string,
  { regionName: string; headline: string; defaultService: string; focusQuote: string }
> = {
  nairobi: {
    regionName: 'Nairobi HQ',
    headline: 'Nairobi Enterprise Web & Software Architecture',
    defaultService: 'Website Development & Custom Laravel ERP',
    focusQuote: 'Looking for a high-speed website and M-Pesa automated cashier system in Nairobi.',
  },
  mombasa: {
    regionName: 'Mombasa Branch',
    headline: 'Mombasa Coast E-Commerce & Web Design',
    defaultService: 'E-Commerce Website Development with M-Pesa STK Push',
    focusQuote: 'Need an online shop or corporate website in Mombasa with Lipa na M-Pesa integration.',
  },
  nakuru: {
    regionName: 'Nakuru Branch',
    headline: 'Nakuru Agricultural & Retail Business Software',
    defaultService: 'Custom Web Design & Inventory POS ERP Software',
    focusQuote: 'Inquiring about website design and custom retail/agribusiness ERP software in Nakuru.',
  },
  kisumu: {
    regionName: 'Kisumu Branch',
    headline: 'Kisumu Lake Region Web & Mobile App Development',
    defaultService: 'Custom Android/iOS Apps & Healthcare/Hospitality Web Systems',
    focusQuote: 'Looking for web developers and mobile app engineering in Kisumu.',
  },
  meru: {
    regionName: 'Meru Branch',
    headline: 'Meru & Mt. Kenya Web Design & Social Media Marketing',
    defaultService: 'Business Website Package & Technical SEO Growth',
    focusQuote: 'Requesting a business website package and digital marketing in Meru.',
  },
};
